import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  VStack,
  Heading,
  Button,
  IconButton,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Flex,
  Text,
  Spinner,
  useToast,
  useColorModeValue,
  Select,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { FaArrowLeft, FaBook, FaTrash, FaSearch } from 'react-icons/fa';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ResponsiveTable from '../components/dashboard/ResponsiveTable';
import MasteryBadge from '../components/dashboard/MasteryBadge';
import TimeAgo from '../components/dashboard/TimeAgo';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import logger from '../utils/logger';

interface Word {
  id: string;
  word: string;
  language: string;
  created_at: string;
  sentence_count?: number;
  mastery?: number;
  last_practiced?: string;
}

export default function Words() {
  // Auth and navigation hooks
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // State for filtering and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Delete confirmation dialog state
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const [wordToDelete, setWordToDelete] = useState<{ id: string; word: string } | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Words query - Get all words from API
  const { data: words, isLoading } = useQuery({
    queryKey: ['words'],
    queryFn: async () => {
      try {
        const wordsData = await apiClient.getWords();

        // Transform to match the expected Word interface
        return wordsData.map(word => ({
          id: word.id,
          word: word.word,
          language: word.language,
          created_at: word.createdAt || word.created_at,
          sentence_count: 0, // TODO: Add sentence count from API
          mastery: 0, // TODO: Add mastery calculation
          last_practiced: null, // TODO: Add practice tracking
        })) as Word[];
      } catch (error) {
        logger.error('Error fetching words:', error);
        return [];
      }
    },
    enabled: !!user,
  });

  // Delete word mutation
  const deleteWord = useMutation({
    mutationFn: async (wordId: string) => {
      await apiClient.deleteWord(wordId);
      return wordId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['words'] });
      toast({
        title: t('words.wordDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      logger.error('Error deleting word:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorOccurred'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Derive available languages from words data
  const availableLanguages = useMemo(() => {
    if (!words || !Array.isArray(words)) return [];

    const languages = Array.from(new Set(words.map(word => word.language)))
      .filter(Boolean)
      .sort();

    return [
      { value: 'all', label: t('common.allLanguages') },
      ...languages.map(lang => ({
        value: lang,
        label: lang.charAt(0).toUpperCase() + lang.slice(1),
      })),
    ];
  }, [words, t]);

  // Filter words based on search query and selected language
  const filteredWords = useMemo(() => {
    if (!words) return [];

    return words.filter((word) => {
      const matchesSearch =
        searchQuery === '' ||
        word.word.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLanguage =
        selectedLanguage === 'all' ||
        word.language.toLowerCase() === selectedLanguage.toLowerCase();

      return matchesSearch && matchesLanguage;
    });
  }, [words, searchQuery, selectedLanguage]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredWords.length / itemsPerPage);
  const paginatedWords = filteredWords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return (
      <Container maxW="container.lg" py={8}>
        <VStack spacing={8} align="center">
          <Spinner size="xl" />
          <Text>{t('common.loading')}</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack spacing={4}>
          <Button
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/dashboard')}
            variant="outline"
          >
            {t('common.back')}
          </Button>
          <Heading flex="1">{t('words.yourWords')}</Heading>
        </HStack>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          mb={4}
          wrap="wrap"
        >
          <InputGroup maxW={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={t('words.searchWords')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>

          <Select
            maxW={{ base: '100%', md: '200px' }}
            value={selectedLanguage}
            onChange={(e) => {
              setSelectedLanguage(e.target.value);
              setCurrentPage(1);
            }}
          >
            {availableLanguages.map(lang => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </Select>
        </Flex>

        <Box
          bg={cardBg}
          borderRadius="lg"
          p={4}
          border="1px"
          borderColor={borderColor}
          overflowX="auto"
        >
          {filteredWords.length > 0 ? (
            <>
              <ResponsiveTable>
                <Thead>
                  <Tr>
                    <Th>{t('common.word')}</Th>
                    <Th>{t('common.language')}</Th>
                    <Th>{t('common.sentences')}</Th>
                    <Th>{t('common.mastery')}</Th>
                    <Th>{t('common.lastPracticed')}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedWords.map((word) => (
                    <Tr key={word.id}>
                      <Td fontWeight="bold">{word.word}</Td>
                      <Td>
                        <Badge colorScheme="blue">
                          {word.language.charAt(0).toUpperCase() + word.language.slice(1)}
                        </Badge>
                      </Td>
                      <Td>{word.sentence_count || 0}</Td>
                      <Td>
                        <MasteryBadge mastery={word.mastery || 0} />
                      </Td>
                      <Td>
                        {word.last_practiced ? (
                          <TimeAgo date={word.last_practiced} />
                        ) : (
                          t('common.never')
                        )}
                      </Td>
                      <Td>
                        <HStack spacing={2} justifyContent="flex-end">
                          <IconButton
                            size="sm"
                            colorScheme="blue"
                            icon={<FaBook />}
                            aria-label={t('words.viewSentences')}
                            onClick={() => navigate(`/words/${word.id}`)}
                          />
                          <IconButton
                            size="sm"
                            colorScheme="red"
                            icon={<FaTrash />}
                            aria-label={t('words.deleteWord')}
                            onClick={() => {
                              setWordToDelete({ id: word.id, word: word.word });
                              onDeleteDialogOpen();
                            }}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </ResponsiveTable>

              {/* Pagination */}
              {totalPages > 1 && (
                <Flex justify="center" mt={6}>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      isDisabled={currentPage === 1}
                    >
                      {t('common.previous')}
                    </Button>

                    {/* Modified pagination with ellipses for many pages */}
                    {(() => {
                      const pageButtons = [];
                      const maxVisibleButtons = 7; // Maximum number of buttons to display
                      const halfVisible = Math.floor(maxVisibleButtons / 2);

                      // Case 1: Not enough pages to need ellipses
                      if (totalPages <= maxVisibleButtons) {
                        for (let i = 1; i <= totalPages; i++) {
                          pageButtons.push(
                            <Button
                              key={i}
                              size="sm"
                              colorScheme={currentPage === i ? "brand" : "gray"}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </Button>
                          );
                        }
                      }
                      // Case 2: Need ellipses - determine which ones
                      else {
                        // Always show first page
                        pageButtons.push(
                          <Button
                            key={1}
                            size="sm"
                            colorScheme={currentPage === 1 ? "brand" : "gray"}
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </Button>
                        );

                        // Calculate start and end of visible pages around current
                        let startPage = Math.max(2, currentPage - halfVisible);
                        let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

                        // Adjust if at boundaries to always show same number of pages
                        if (startPage <= 2) {
                          endPage = Math.min(totalPages - 1, maxVisibleButtons - 2);
                          startPage = 2;
                        }
                        if (endPage >= totalPages - 1) {
                          startPage = Math.max(2, totalPages - maxVisibleButtons + 1);
                          endPage = totalPages - 1;
                        }

                        // Show ellipsis before start if needed
                        if (startPage > 2) {
                          pageButtons.push(
                            <Text key="ellipsis-start" mx={1}>...</Text>
                          );
                        }

                        // Show the middle pages
                        for (let i = startPage; i <= endPage; i++) {
                          pageButtons.push(
                            <Button
                              key={i}
                              size="sm"
                              colorScheme={currentPage === i ? "brand" : "gray"}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </Button>
                          );
                        }

                        // Show ellipsis after end if needed
                        if (endPage < totalPages - 1) {
                          pageButtons.push(
                            <Text key="ellipsis-end" mx={1}>...</Text>
                          );
                        }

                        // Always show last page
                        pageButtons.push(
                          <Button
                            key={totalPages}
                            size="sm"
                            colorScheme={currentPage === totalPages ? "brand" : "gray"}
                            onClick={() => handlePageChange(totalPages)}
                          >
                            {totalPages}
                          </Button>
                        );
                      }

                      return pageButtons;
                    })()}

                    <Button
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      isDisabled={currentPage === totalPages}
                    >
                      {t('common.next')}
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          ) : (
            <Box textAlign="center" py={10}>
              <Text>{t('words.noWords')}</Text>
              <Button
                mt={4}
                colorScheme="brand"
                onClick={() => navigate('/dashboard')}
              >
                {t('common.backToDashboard')}
              </Button>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={onDeleteDialogClose}
        onConfirm={() => {
          if (wordToDelete) {
            deleteWord.mutate(wordToDelete.id);
          }
        }}
        title={t('words.deleteConfirm.title')}
        message={t('words.deleteConfirm.message')}
        confirmText={t('words.deleteConfirm.confirm')}
        cancelText={t('words.deleteConfirm.cancel')}
        isLoading={deleteWord.isPending}
        loadingText={t('common.deleting')}
      />
    </Container>
  );
}